import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Briefcase, Microscope, Users, Globe, CheckCircle2, AlertCircle } from "lucide-react";
import { postOpportunity } from "../lib/api";
import PageHero from "../components/PageHero";
import { Kicker, Reveal } from "../components/motion";

const CATEGORIES = [
  { key: "research", Icon: Microscope },
  { key: "internship", Icon: Briefcase },
  { key: "conference", Icon: Users },
  { key: "collaboration", Icon: Globe },
];

const EMPTY = { title: "", opportunity_type: "research", organization: "", description: "", link: "", deadline: "", submitter_name: "", submitter_email: "" };

export default function Opportunities() {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true); setStatus(null);
    try {
      await postOpportunity(form);
      setStatus("ok");
      setForm(EMPTY);
    } catch {
      setStatus("err");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="page-opportunities">
      <PageHero testId="opportunities-hero" kicker={t("opportunities.kicker")} title={t("opportunities.headline")} lead={t("opportunities.lead")} />

      <section className="surface-2" data-testid="opportunities-categories">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {CATEGORIES.map(({ key, Icon }, i) => (
            <Reveal key={key} delay={i * 0.08}>
              <div className="group gxr-card p-10 h-full" data-testid={`opportunities-category-${key}`}>
                <span className="icon-tile"><Icon className="h-5 w-5" /></span>
                <div className="font-display text-2xl lg:text-3xl mt-6 text-ink">{t(`opportunities.categories.${key}`)}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="panel-forest border-y border-line" data-testid="opportunities-submit-strip">
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <Reveal className="lg:col-span-8">
            <Kicker light>{t("opportunities.submit_kicker")}</Kicker>
            <h2 className="font-display text-5xl lg:text-7xl mt-6 leading-[1] text-white">{t("opportunities.submit_title")}</h2>
            <p className="mt-8 text-white/75 max-w-2xl text-lg lg:text-xl">{t("opportunities.submit_body")}</p>
          </Reveal>
          <div className="lg:col-span-4 lg:text-end">
            <button onClick={() => setShowForm((v) => !v)} className="btn-gold" data-testid="opportunities-toggle-form-button">
              {showForm ? "—" : "+"} {t("opportunities.submit_cta")}
            </button>
          </div>
        </div>
      </section>

      {showForm && (
        <section className="surface-1" data-testid="opportunities-form-section">
          <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-20">
            <form onSubmit={onSubmit} className="paper p-8 lg:p-12 space-y-8" data-testid="opportunities-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                <div>
                  <label className="gxr-label" htmlFor="opp-title">{t("opportunities.form.title")} *</label>
                  <input id="opp-title" required value={form.title} onChange={set("title")} className="gxr-input" data-testid="opp-title-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="opp-type">{t("opportunities.form.type")} *</label>
                  <select id="opp-type" value={form.opportunity_type} onChange={set("opportunity_type")} className="gxr-input bg-transparent" data-testid="opp-type-select">
                    {["research", "internship", "conference", "collaboration", "other"].map((k) => (
                      <option key={k} value={k}>{t(`opportunities.categories.${k}`)}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="gxr-label" htmlFor="opp-org">{t("opportunities.form.organization")} *</label>
                  <input id="opp-org" required value={form.organization} onChange={set("organization")} className="gxr-input" data-testid="opp-org-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="opp-deadline">{t("opportunities.form.deadline")}</label>
                  <input id="opp-deadline" value={form.deadline} onChange={set("deadline")} className="gxr-input" data-testid="opp-deadline-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="gxr-label" htmlFor="opp-link">{t("opportunities.form.link")}</label>
                  <input id="opp-link" type="url" value={form.link} onChange={set("link")} className="gxr-input" placeholder="https://" data-testid="opp-link-input" />
                </div>
                <div className="md:col-span-2">
                  <label className="gxr-label" htmlFor="opp-desc">{t("opportunities.form.description")} *</label>
                  <textarea id="opp-desc" required rows={5} value={form.description} onChange={set("description")} className="gxr-input resize-y" data-testid="opp-desc-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="opp-name">{t("opportunities.form.your_name")} *</label>
                  <input id="opp-name" required value={form.submitter_name} onChange={set("submitter_name")} className="gxr-input" data-testid="opp-name-input" />
                </div>
                <div>
                  <label className="gxr-label" htmlFor="opp-email">{t("opportunities.form.your_email")} *</label>
                  <input id="opp-email" type="email" required value={form.submitter_email} onChange={set("submitter_email")} className="gxr-input" data-testid="opp-email-input" />
                </div>
              </div>

              <div className="flex items-center gap-4 pt-2 flex-wrap">
                <button type="submit" disabled={submitting} className="btn-forest" data-testid="opp-submit-button">
                  {submitting ? t("common.sending") : t("common.submit")} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
                </button>
                {status === "ok" && (
                  <div className="flex items-center gap-2 text-forest" data-testid="opp-success"><CheckCircle2 className="h-5 w-5" /> {t("common.thankYou")}</div>
                )}
                {status === "err" && (
                  <div className="flex items-center gap-2 text-red-600" data-testid="opp-error"><AlertCircle className="h-5 w-5" /> {t("common.errorGeneric")}</div>
                )}
              </div>
            </form>
          </div>
        </section>
      )}
    </div>
  );
}
